import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, LayoutDashboard, LogOut, HeartPulse, Menu, Globe, FileText } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { DesktopNav, MobileNav } from "./NavLinks";
import { useCart } from "../context/CartContext";
import { useQuote } from "../context/QuoteContext";
import { useCurrency } from "../context/CurrencyContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const FLAGS = { XOF: "🇨🇮", EUR: "🇪🇺", USD: "🇺🇸", CNY: "🇨🇳", RUB: "🇷🇺", GBP: "🇬🇧", CAD: "🇨🇦", CHF: "🇨🇭", MAD: "🇲🇦", NGN: "🇳🇬", GHS: "🇬🇭" };

export function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: quoteCount } = useQuote();
  const { code, setCode, labels } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const navCls = (to) => { const [path, hash] = to.split("#"); const active = hash ? location.pathname === (path || "/") && location.hash === `#${hash}` : location.pathname === to || (to !== "/" && location.pathname.startsWith(to + "/")); return `nav-link${active ? " nav-link-active" : ""}`; };
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy text-white">
            <HeartPulse size={18} />
          </span>
          <span className="hidden whitespace-nowrap font-display text-base font-bold tracking-tight text-navy sm:inline">
            Vitalis <span className="text-ciel">Digital Store</span>
          </span>
        </Link>

        <DesktopNav user={user} navCls={navCls} />

        <div className="flex items-center gap-1 sm:gap-3">
          <Select value={code} onValueChange={setCode}>
            <SelectTrigger data-testid="currency-selector" title={labels[code]} className="h-9 w-auto gap-1.5 rounded-full border-slate-200 bg-white px-2 text-xs font-semibold text-navy hover:border-ciel sm:px-3">
              <span aria-hidden>{FLAGS[code] || "🌍"}</span>
              <SelectValue>{code === "XOF" ? "FCFA" : code}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {Object.keys(labels).map((c) => (
                <SelectItem key={c} value={c} data-testid={`currency-${c}`}>
                  {FLAGS[c] || "🌍"} {c === "XOF" ? "FCFA" : c} — {labels[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Link to="/devis" data-testid="nav-quote" title="Demande de devis" className="relative p-2 text-slate-600 transition-colors duration-200 hover:text-ciel">
            <FileText size={20} strokeWidth={1.8} />
            {quoteCount > 0 && (
              <span data-testid="nav-quote-count" className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-navy font-mono text-[10px] font-semibold text-white">
                {quoteCount}
              </span>
            )}
          </Link>
          <Link to="/panier" data-testid="nav-cart" className="relative p-2 text-slate-600 transition-colors duration-200 hover:text-ciel">
            <ShoppingCart size={20} strokeWidth={1.8} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-ciel font-mono text-[10px] font-semibold text-white">
                {count}
              </span>
            )}
          </Link>

          {user && <NotificationBell />}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 text-sm font-medium text-navy transition-colors duration-200 hover:border-ciel">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-navy font-mono text-[10px] uppercase text-white">
                    {user.name?.slice(0, 2)}
                  </span>
                  <span className="hidden max-w-[100px] truncate sm:inline">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {user.role === "admin" ? (
                  <DropdownMenuItem data-testid="nav-dashboard-link" onClick={() => navigate("/admin")} className="cursor-pointer gap-2">
                    <LayoutDashboard size={14} /> Tableau de bord
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem data-testid="nav-dashboard-link" onClick={() => navigate("/compte")} className="cursor-pointer gap-2">
                    <LayoutDashboard size={14} /> Mon espace client
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="nav-logout" onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer gap-2 text-red-600">
                  <LogOut size={14} /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/auth"
              data-testid="nav-login"
              className="rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-navy-light sm:px-5 sm:text-sm"
            >
              Connexion
            </Link>
          )}
          <button data-testid="nav-mobile-menu" className="p-2 text-slate-600 md:hidden" onClick={() => setOpen(!open)}>
            <Menu size={20} />
          </button>
        </div>
      </div>
      {open && <MobileNav user={user} onClose={() => setOpen(false)} />}
    </header>
  );
}
